import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useTileCache,
  MIN_ZOOM_FOR_TILES,
  TileFilterParams,
} from './useTileCache'
import {
  IUserPreferenceGeneralListItemType,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import {
  applyClientSideRefilters,
  AreaParams,
  getContactLocationsInArea,
  resolveSearchMatch,
  selectOverrideFilters,
  setContactLocations,
} from '@user/store/map'
import {
  excludeClosedLinkedSalesCenterPins,
  excludeKnockStatusPinsCoveredByPestPac,
  excludeLinkedPestPacPins,
  fetchMapConfig,
  fetchPestPacLocations,
  mergeColocatedSalesCenterAndPestPacPins,
} from '@user/store/map/api'
import { useAppDispatch, useAppSelector } from '@user/store/hooks'
import { logger } from '@user/utils/logger'
import { toast } from '@sales-center/components'
import {
  selectAllKnockStatuses,
  selectKnockStatusesLoaded,
  selectKnockStatusDeletedAt,
  selectKnockStatusSavedAt,
} from '@user/store/knockStatus/slice'
import { selectUserPreferencesGeneralLoaded } from '@user/store/userPreferences/userPreferencesGeneral'
import { usePestPacPinsEnabled } from '@user/store/tenantFeatures/hooks/usePestPacPinsEnabled'
import { useSalesPersonFilterEnabled } from '@user/store/tenantFeatures/hooks/useSalesPersonFilterEnabled'

const TILE_CACHE_ENABLED = process.env.REACT_APP_TILE_CACHE_ENABLED !== 'false'

type Props = {
  entityId?: number
  entityType?: string
  serviceLocationId?: number
  foundLocationFromSearch?: boolean
}

export const useApi = ({ foundLocationFromSearch }: Props) => {
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const preferencesLoaded = useSelector(selectUserPreferencesGeneralLoaded)
  const currentUser = useSelector(selectCurrentUser)
  const overrideFilters = useSelector(selectOverrideFilters)
  const knockStatuses = useAppSelector(selectAllKnockStatuses)
  const knockStatusesLoaded = useAppSelector(selectKnockStatusesLoaded)
  const knockStatusDeletedAt = useAppSelector(selectKnockStatusDeletedAt)
  const knockStatusSavedAt = useAppSelector(selectKnockStatusSavedAt)
  const pestPacPinsEnabled = usePestPacPinsEnabled()
  const salesPersonFilterEnabled = useSalesPersonFilterEnabled()
  const [isRendering, setIsRendering] = useState<boolean>(true)
  const { refresh: tileRefresh, evictTile, evictAll } = useTileCache()

  const mapViewFilteredEntityTypesPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type ===
      IUserPreferenceGeneralListItemType.MapViewFilteredEntityTypes,
  )
  const mapViewFilteredMyRecordsPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type === IUserPreferenceGeneralListItemType.MapViewFilteredMyRecords,
  )
  const mapViewFilteredKnockStatusesPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type ===
      IUserPreferenceGeneralListItemType.MapViewFilteredKnockStatuses,
  )
  const mapViewFilteredEntityTypesPreferenceValue =
    mapViewFilteredEntityTypesPreference?.value ?? 'draftlead_lead_opportunity'

  const cleanEntityTypes = mapViewFilteredEntityTypesPreferenceValue
    .split('_')
    .filter(Boolean)
    .join('_')

  const myRecordsOnlyPreferenceValue =
    mapViewFilteredMyRecordsPreference?.value ?? 'true'

  // SH-2168: the salesperson filter replaces the "my records only"
  // preference entirely — never restrict the fetch to the current user
  // while it's enabled, regardless of that (now-unused) persisted value.
  const effectiveOwnerId = salesPersonFilterEnabled
    ? null
    : myRecordsOnlyPreferenceValue === 'true'
      ? currentUser.userId
      : null

  const knockStatusIdsPreferenceValue =
    mapViewFilteredKnockStatusesPreference?.value ?? ''

  const hasKnockStatusFilter =
    knockStatusIdsPreferenceValue !== '' &&
    knockStatusIdsPreferenceValue !== 'none'

  const validKnockStatusIds = hasKnockStatusFilter
    ? knockStatusIdsPreferenceValue
        .split('_')
        .filter(Boolean)
        .filter((id) => knockStatuses.some((ks) => String(ks.id) === id))
        .join('_')
    : ''

  const cleanKnockStatusIds = validKnockStatusIds || undefined

  const dispatch = useAppDispatch()
  const lastParamsRef = useRef<AreaParams | null>(null)
  const lastTileParamsRef = useRef<{
    topLeftLat: number
    topLeftLng: number
    bottomRightLat: number
    bottomRightLng: number
    zoom: number
    entityTypes: string
    ownerId: string | null
    overrideFilters: boolean
    knockStatusIds: string | undefined
    pestPacPinsEnabled: boolean
  } | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Fetched once on mount — mirrors useMapLibreInstance's fetchMapConfig pattern.
  // Default (11) matches MapSettings.PestPacMapSearchMinZoom until config loads.
  const pestPacMinZoomRef = useRef<number>(11)

  useEffect(() => {
    let isMounted = true
    fetchMapConfig().then((config) => {
      if (!isMounted || !config) return
      pestPacMinZoomRef.current = config.pestPacMinZoom
    })
    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const refreshMapPins = useCallback(
    async (
      forceUpdate: boolean,
      topLeft?: H.geo.Point,
      bottomRight?: H.geo.Point,
      _center?: H.geo.Point,
      zoom?: number,
    ) => {
      const knockStatusLocationsOnly =
        cleanEntityTypes === '' && !!cleanKnockStatusIds

      const effectiveEntityTypes = knockStatusLocationsOnly
        ? 'draftlead_lead_opportunity'
        : cleanEntityTypes

      // PestPac pins load regardless of other filters, but only once zoomed in
      // enough to keep the extra external request volume down (SH-586).
      const pestPacActive =
        pestPacPinsEnabled && (zoom ?? 14) >= pestPacMinZoomRef.current

      // ── Tile-cache path ────────────────────────────────────────────────────
      const useTilePath =
        TILE_CACHE_ENABLED &&
        (zoom ?? 14) >= MIN_ZOOM_FOR_TILES &&
        !foundLocationFromSearch &&
        !overrideFilters

      if (
        useTilePath &&
        topLeft &&
        bottomRight &&
        preferencesLoaded &&
        (!hasKnockStatusFilter || knockStatusesLoaded)
      ) {
        const filterParams: TileFilterParams = {
          entityTypes: cleanEntityTypes,
          ownerId: effectiveOwnerId,
          overrideFilters,
          knockStatusIds: cleanKnockStatusIds,
          pestPacPinsEnabled: pestPacActive,
        }

        if (!forceUpdate) {
          const last = lastTileParamsRef.current
          if (
            last &&
            last.topLeftLat === topLeft.lat &&
            last.topLeftLng === topLeft.lng &&
            last.bottomRightLat === bottomRight.lat &&
            last.bottomRightLng === bottomRight.lng &&
            Math.floor(last.zoom) === Math.floor(zoom ?? 14) &&
            last.entityTypes === filterParams.entityTypes &&
            last.ownerId === filterParams.ownerId &&
            last.overrideFilters === filterParams.overrideFilters &&
            last.knockStatusIds === filterParams.knockStatusIds &&
            last.pestPacPinsEnabled === filterParams.pestPacPinsEnabled
          )
            return
        }

        lastTileParamsRef.current = {
          topLeftLat: topLeft.lat,
          topLeftLng: topLeft.lng,
          bottomRightLat: bottomRight.lat,
          bottomRightLng: bottomRight.lng,
          zoom: zoom ?? 14,
          entityTypes: filterParams.entityTypes,
          ownerId: filterParams.ownerId,
          overrideFilters: filterParams.overrideFilters,
          knockStatusIds: filterParams.knockStatusIds,
          pestPacPinsEnabled: filterParams.pestPacPinsEnabled,
        }

        if (forceUpdate) evictAll()
        setIsRendering(true)
        await tileRefresh(
          topLeft.lat,
          topLeft.lng,
          bottomRight.lat,
          bottomRight.lng,
          zoom ?? 14,
          filterParams,
        )
        setIsRendering(false)
        return
      }
      // ──────────────────────────────────────────────────────────────────────

      const params: AreaParams = {
        long1: topLeft?.lng ?? 0,
        lat1: topLeft?.lat ?? 0,
        long2: bottomRight?.lng ?? 0,
        lat2: bottomRight?.lat ?? 0,
        zoom: zoom || 14,
        foundLocationFromSearch: !!foundLocationFromSearch,
        entityTypes: effectiveEntityTypes,
        ownerId: effectiveOwnerId,
        overrideFilters,
        forceUpdate,
        knockStatusIds: cleanKnockStatusIds,
        includeRecordsWithoutKnockStatus:
          cleanEntityTypes !== '' && !!cleanKnockStatusIds,
        knockStatusLocationsOnly,
        pestPacActive,
      }

      const last = lastParamsRef.current
      if (
        !forceUpdate &&
        last &&
        !params.foundLocationFromSearch &&
        last.long1 === params.long1 &&
        last.lat1 === params.lat1 &&
        last.long2 === params.long2 &&
        last.lat2 === params.lat2 &&
        last.zoom === params.zoom &&
        last.ownerId === params.ownerId &&
        last.entityTypes === params.entityTypes &&
        last.overrideFilters === params.overrideFilters &&
        last.knockStatusIds === params.knockStatusIds &&
        last.knockStatusLocationsOnly === params.knockStatusLocationsOnly
      ) {
        return
      }

      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setIsRendering(true)
      const result = await dispatch(
        getContactLocationsInArea({ ...params, signal: controller.signal }),
      )
      if (controller.signal.aborted) return
      if (getContactLocationsInArea.fulfilled.match(result)) {
        lastParamsRef.current = params

        // Tile-cache path aside, this (legacy) path is used for low zoom,
        // overrideFilters, and foundLocationFromSearch — PestPac pins still
        // appear here, but now respect the same knock-status filter as
        // Sales Center pins (SH-1548; previously ignored it entirely, an
        // unresolved gap flagged but never confirmed intentional in SH-586).
        //
        // PR review: the deferred (PestPac-merged) resolveSearchMatch dispatches below
        // reuse result.payload.filteredKnockStatusNames — already resolved by the thunk
        // against the state at fetch-completion time (api.ts) — rather than recomputing
        // it here against the component-level knockStatuses selector, so the two can't
        // disagree if the knock-status settings list changes while this fetch is in flight.
        if (pestPacActive && topLeft && bottomRight) {
          // The bbox this search's address match was deferred against (slice.ts
          // skips matching in getContactLocationsInArea.fulfilled whenever
          // overrideFilters && pestPacActive, since result.payload.locations is
          // Sales-Center-only and never contains a searched PestPac record).
          // resolveSearchMatch below re-runs that match against the real,
          // merged data once it's available.
          const searchBbox = {
            lat1: params.lat1,
            lat2: params.lat2,
            long1: params.long1,
            long2: params.long2,
          }
          const fallBackToSalesCenterOnly = () => {
            // A non-OK response or unparseable body. Fall back to Sales
            // Center-only pins rather than leaving a stale PestPac
            // carry-forward in state indefinitely — otherwise a carried
            // pin re-carries itself on every subsequent pan while search
            // mode persists, and a stale pin can even win the address
            // match in setSearchTargetLocation on the next search.
            toast.error('Failed to load PestPac locations')
            dispatch(setContactLocations(result.payload.locations))
            if (overrideFilters) {
              dispatch(
                resolveSearchMatch({
                  locations: result.payload.locations,
                  bbox: searchBbox,
                  entityTypes: params.entityTypes,
                  filteredKnockStatusNames:
                    result.payload.filteredKnockStatusNames,
                  knockStatusLocationsOnly: params.knockStatusLocationsOnly,
                }),
              )
            }
          }
          try {
            // PestPac pins load regardless of the entity-type filter (SH-586) — a status-less
            // PestPac pin must stay visible even in "Knock Status Locations Only" mode
            // (cleanEntityTypes === ''), unlike a status-less Sales Center record. Passing
            // `true` unconditionally here (rather than gating on cleanEntityTypes, as the
            // Sales Center includeRecordsWithoutKnockStatus param does) keeps it that way; an
            // explicit, non-matching knock status still hides a PestPac pin via knockStatusIds.
            const pestPacResult = await fetchPestPacLocations(
              {
                long1: params.long1,
                lat1: params.lat1,
                long2: params.long2,
                lat2: params.lat2,
              },
              zoom ?? 14,
              cleanKnockStatusIds,
              true,
            )
            if (controller.signal.aborted) {
              // superseded — do nothing
            } else if (!pestPacResult.ok) {
              fallBackToSalesCenterOnly()
            } else {
              // An empty result is a legitimate response (e.g. no PestPac
              // locations in this area). It must still dispatch when a carry
              // could have happened (overrideFilters — pestPacActive is
              // already guaranteed true here — is the same gate the slice
              // reducer's mergePestPacPinsForward uses) so it clears any pins
              // carried forward from a prior fetch (SH-1341). Otherwise skip:
              // the reconciled set would just reproduce result.payload.locations,
              // and dispatching it anyway would force a redundant re-render.
              const pestPacLocations = pestPacResult.locations
              const salesCenterLocations = result.payload.locations
              // overrideFilters makes salesCenterLocations include every entity type, even
              // ones the active filter hides — used as-is below (excludeClosedLinkedSalesCenterPins/
              // excludeKnockStatusPinsCoveredByPestPac only ever narrow the SC side, they don't
              // decide a PestPac pin's fate). excludeLinkedPestPacPins is different: it suppresses a
              // PestPac pin whenever its account is linked to *any* SC pin passed in, on the
              // assumption that SC pin will render. Passing it the post-refilter set instead keeps a
              // PestPac pin whose only linked SC record is about to be filtered back out anyway
              // (SH-2100 — otherwise neither pin ends up representing that address). Must include
              // knockStatusLocationsOnly: params.entityTypes is widened to every record type
              // whenever that mode is active (so the backend still returns them for search-match
              // preservation), so entityTypes alone no longer excludes a linked non-KS record here
              // (Copilot review finding).
              const visibleSalesCenterLocations = applyClientSideRefilters(
                salesCenterLocations,
                {
                  entityTypes: params.entityTypes,
                  filteredKnockStatusNames:
                    result.payload.filteredKnockStatusNames,
                  knockStatusLocationsOnly: params.knockStatusLocationsOnly,
                },
                null,
              )
              if (pestPacLocations.length > 0 || overrideFilters) {
                const mergedLocations = mergeColocatedSalesCenterAndPestPacPins(
                  excludeKnockStatusPinsCoveredByPestPac(
                    excludeClosedLinkedSalesCenterPins(
                      salesCenterLocations,
                      pestPacLocations,
                    ),
                    pestPacLocations,
                  ),
                  excludeLinkedPestPacPins(
                    visibleSalesCenterLocations,
                    pestPacLocations,
                  ),
                )
                dispatch(setContactLocations(mergedLocations))
                if (overrideFilters) {
                  dispatch(
                    resolveSearchMatch({
                      locations: mergedLocations,
                      bbox: searchBbox,
                      entityTypes: params.entityTypes,
                      filteredKnockStatusNames:
                        result.payload.filteredKnockStatusNames,
                      knockStatusLocationsOnly: params.knockStatusLocationsOnly,
                    }),
                  )
                }
              }
            }
          } catch {
            // Non-blocking: Sales Center pins must still render even if the
            // PestPac tile fetch throws outright (e.g. network error). Skip
            // the toast if this request has since been superseded — a stale
            // error is not worth surfacing. Always show a generic message —
            // the underlying error (e.g. "Failed to fetch") isn't user-facing.
            // Mirrors the !ok branch above: drop any stale PestPac
            // carry-forward instead of leaving it in state indefinitely
            // (SH-1341 review — thrown fetches are the more common failure
            // mode and were previously left unhandled here).
            if (!controller.signal.aborted) {
              fallBackToSalesCenterOnly()
            }
          }
        }
      } else if (
        getContactLocationsInArea.rejected.match(result) &&
        result.payload?.StatusCode !== 0
      ) {
        logger.error('Error refreshing map pins:', result.payload)
      }
      if (!controller.signal.aborted) {
        setIsRendering(false)
      }
    },
    [
      dispatch,
      foundLocationFromSearch,
      cleanEntityTypes,
      effectiveOwnerId,
      cleanKnockStatusIds,
      pestPacPinsEnabled,
      overrideFilters,
      preferencesLoaded,
      knockStatusesLoaded,
      hasKnockStatusFilter,
      tileRefresh,
      evictAll,
    ],
  )

  // Calls refreshMapPins(forceUpdate=true) using whichever bounds ref is current.
  // Tile-path bounds (lastTileParamsRef) take priority — they reflect the actual
  // current viewport. Legacy bounds (lastParamsRef) are only used as a fallback
  // for low-zoom / override-filter sessions where the tile path is inactive.
  // Using stale legacy bounds on the tile path would evict the correct tile cache
  // and re-fetch a different viewport area, making nearby pins disappear (SH-883).
  const forceRefreshWithCurrentBounds = useCallback(() => {
    const tileParams = lastTileParamsRef.current
    if (tileParams) {
      console.log(
        '[SH-883] KS refresh using tile path bounds, zoom:',
        tileParams.zoom,
      )
      refreshMapPins(
        true,
        {
          lat: tileParams.topLeftLat,
          lng: tileParams.topLeftLng,
        } as H.geo.Point,
        {
          lat: tileParams.bottomRightLat,
          lng: tileParams.bottomRightLng,
        } as H.geo.Point,
        undefined,
        tileParams.zoom,
      )
      return
    }
    if (!lastParamsRef.current) return
    const { long1, lat1, long2, lat2, zoom } = lastParamsRef.current
    console.log('[SH-883] KS refresh using legacy path bounds, zoom:', zoom)
    refreshMapPins(
      true,
      { lat: lat1, lng: long1 } as H.geo.Point,
      { lat: lat2, lng: long2 } as H.geo.Point,
      undefined,
      zoom,
    )
  }, [refreshMapPins])

  const prevOverrideFiltersRef = useRef(overrideFilters)
  useEffect(() => {
    const wasOverriding = prevOverrideFiltersRef.current
    prevOverrideFiltersRef.current = overrideFilters

    // Leaving override mode (the search/found-record session ending) — the override-scoped
    // legacy fetch replaced whatever the tile cache had accumulated for the viewport, and
    // nothing else automatically re-fetches at the current bounds once overrideFilters no
    // longer disables the tile path. Without this, previously visible pins outside that
    // narrower search scope stayed missing until the whole map view was remounted.
    //
    // refreshMapPins runs synchronously up to its first await, so this call already writes
    // a fresh value into whichever dedup ref (tile or legacy) it uses before returning here —
    // resetting both refs afterward (as this effect otherwise does below) would wipe that
    // fresh value and force a redundant extra fetch on the very next pan. Return early instead
    // of falling through to the reset.
    if (wasOverriding && !overrideFilters) {
      forceRefreshWithCurrentBounds()
      return
    }

    // Entering override mode (or, defensively, any other toggle): reset dedup caches so the
    // very next refreshMapPins call always fires a fresh API request rather than being
    // skipped as a duplicate of the last non-search viewport.
    lastParamsRef.current = null
    lastTileParamsRef.current = null
  }, [overrideFilters, forceRefreshWithCurrentBounds])

  const isFirstDeleteRenderRef = useRef(true)
  useEffect(() => {
    if (isFirstDeleteRenderRef.current) {
      isFirstDeleteRenderRef.current = false
      return
    }
    forceRefreshWithCurrentBounds()
  }, [knockStatusDeletedAt])

  const isFirstSaveRenderRef = useRef(true)
  useEffect(() => {
    if (isFirstSaveRenderRef.current) {
      isFirstSaveRenderRef.current = false
      return
    }
    console.log(
      '[SH-883] knockStatusSavedAt fired, tileRef:',
      lastTileParamsRef.current?.zoom,
      'legacyRef:',
      lastParamsRef.current?.zoom,
    )
    forceRefreshWithCurrentBounds()
  }, [knockStatusSavedAt])

  return { refreshMapPins, isRendering, evictTile }
}
